/**
 * Il cuore della fase 1.
 *
 * «Su una TV la lentezza non si misura in fotogrammi al secondo: si misura in ritardo fra
 * la pressione della freccia e lo spostamento del riquadro di selezione. Sopra i 100 ms
 * la persona preme due volte; sopra i 200 salta una riga e si perde» (07-PRESTAZIONI §6).
 *
 * Da cui la forma di questa classe, che è l'unica cosa che conta di tutto il file:
 *
 *  - **due flussi di notifica separati.** `onCursorChange` scatta a ogni freccia e serve
 *    a spostare un rettangolo; `onWindowChange` scatta **solo quando cambia l'insieme
 *    delle righe da disegnare**. Una freccia dentro le righe già disegnate — cioè la
 *    stragrande maggioranza — non fa ri-rendere niente.
 *  - **lo scorrimento è minimo.** `scrollToReveal` non muove niente se l'elemento è già
 *    visibile con il suo margine, quindi scendere di una riga dentro lo schermo non
 *    cambia la finestra e non ridisegna.
 *  - **nessun caricamento mentre il fuoco si sposta.** Il caricamento aspetta che il dito
 *    si fermi: `settleMs`, e chi ascolta `onSettle` è l'unico che ha diritto di chiedere
 *    qualcosa alla rete.
 *
 * Questa classe non tocca il DOM e non conosce React: prende decisioni. Chi le attua —
 * scrivere un `transform`, montare le righe — sta in `GuideGrid.tsx`.
 */

import {
  ROW_PITCH_PX,
  computeVisibleRange,
  layoutProgram,
  scrollToReveal,
  scrollToRevealSpan,
  windowWidthPx,
  type Channel,
  type GuideWindow,
  type ProgramStore,
  type VisibleRange,
} from '@player/domain';

export interface GuideCursor {
  readonly channelIndex: number;
  /** Lo slot del programma dentro la riga, riferito all'intera riga e non alla finestra. */
  readonly slot: number;
}

export interface GuideViewport {
  readonly heightPx: number;
  readonly timelineWidthPx: number;
}

export interface GuideGeometry {
  readonly range: VisibleRange;
  readonly scrollYPx: number;
  readonly scrollXPx: number;
}

export interface GuideControllerOptions {
  readonly channels: ReadonlyArray<Channel>;
  readonly programs: ProgramStore;
  readonly window: GuideWindow;
  readonly viewport: GuideViewport;
  /**
   * Margine di righe disegnate fuori campo. Due sopra e due sotto: abbastanza perché la
   * freccia successiva trovi la riga già pronta, poco abbastanza da non pesare. È anche
   * il numero che tiene le locandine in memoria sotto le ottanta di 07-PRESTAZIONI §2.
   */
  readonly overscanRows?: number;
  /** Quanto il fuoco deve stare lontano dal bordo prima che la lista scorra. */
  readonly edgeMarginPx?: number;
  /** Attesa prima di dichiarare che il dito si è fermato. */
  readonly settleMs?: number;
  readonly setTimeoutImpl?: (callback: () => void, ms: number) => unknown;
  readonly clearTimeoutImpl?: (handle: unknown) => void;
}

export class GuideController {
  private readonly channels: ReadonlyArray<Channel>;
  private readonly programs: ProgramStore;
  private readonly window: GuideWindow;
  private viewport: GuideViewport;
  private readonly overscanRows: number;
  private readonly edgeMarginPx: number;
  private readonly settleMs: number;
  private readonly setTimeoutImpl: (callback: () => void, ms: number) => unknown;
  private readonly clearTimeoutImpl: (handle: unknown) => void;

  private cursor: GuideCursor = { channelIndex: 0, slot: 0 };
  private scrollYPx = 0;
  private scrollXPx = 0;
  private range: VisibleRange;
  private settleHandle: unknown = null;

  private readonly cursorListeners = new Set<(cursor: GuideCursor) => void>();
  private readonly windowListeners = new Set<(geometry: GuideGeometry) => void>();
  private readonly settleListeners = new Set<(cursor: GuideCursor) => void>();

  /** Contatori per il banco di misura: quante notifiche di ciascun tipo. Il rapporto fra
   *  i due è la dimostrazione che il fuoco non ridisegna la griglia. */
  private cursorNotifications = 0;
  private windowNotifications = 0;

  constructor(options: GuideControllerOptions) {
    this.channels = options.channels;
    this.programs = options.programs;
    this.window = options.window;
    this.viewport = options.viewport;
    this.overscanRows = options.overscanRows ?? 2;
    this.edgeMarginPx = options.edgeMarginPx ?? ROW_PITCH_PX;
    this.settleMs = options.settleMs ?? 250;
    this.setTimeoutImpl = options.setTimeoutImpl ?? ((callback, ms) => setTimeout(callback, ms));
    this.clearTimeoutImpl =
      options.clearTimeoutImpl ?? ((handle) => clearTimeout(handle as ReturnType<typeof setTimeout>));
    this.range = this.computeRange();
  }

  get counters(): { cursorNotifications: number; windowNotifications: number } {
    return {
      cursorNotifications: this.cursorNotifications,
      windowNotifications: this.windowNotifications,
    };
  }

  resetCounters(): void {
    this.cursorNotifications = 0;
    this.windowNotifications = 0;
  }

  get currentCursor(): GuideCursor {
    return this.cursor;
  }

  get geometry(): GuideGeometry {
    return { range: this.range, scrollYPx: this.scrollYPx, scrollXPx: this.scrollXPx };
  }

  get channelCount(): number {
    return this.channels.length;
  }

  channelAt(index: number): Channel | null {
    return this.channels[index] ?? null;
  }

  onCursorChange(listener: (cursor: GuideCursor) => void): () => void {
    this.cursorListeners.add(listener);
    return () => this.cursorListeners.delete(listener);
  }

  onWindowChange(listener: (geometry: GuideGeometry) => void): () => void {
    this.windowListeners.add(listener);
    return () => this.windowListeners.delete(listener);
  }

  /** Chi ascolta qui è l'unico che può chiedere qualcosa alla rete. */
  onSettle(listener: (cursor: GuideCursor) => void): () => void {
    this.settleListeners.add(listener);
    return () => this.settleListeners.delete(listener);
  }

  setViewport(viewport: GuideViewport): void {
    this.viewport = viewport;
    this.applyGeometry();
  }

  private computeRange(): VisibleRange {
    return computeVisibleRange(
      this.scrollYPx,
      this.viewport.heightPx,
      ROW_PITCH_PX,
      this.channels.length,
      this.overscanRows,
    );
  }

  /**
   * Muove il fuoco.
   *
   * Verticale: cambia canale, e il programma su cui atterra è **quello che copre lo
   * stesso istante** del programma che si lasciava. È la differenza fra una guida che si
   * segue con gli occhi e una in cui scendendo si finisce a un'ora a caso.
   */
  move(direction: 'up' | 'down' | 'left' | 'right'): boolean {
    const before = this.cursor;
    let next: GuideCursor = before;

    if (direction === 'left' || direction === 'right') {
      const count = this.programs.programCountOf(before.channelIndex);
      const slot = before.slot + (direction === 'right' ? 1 : -1);
      if (slot < 0 || slot >= count) return false;
      next = { channelIndex: before.channelIndex, slot };
    } else {
      const channelIndex = before.channelIndex + (direction === 'down' ? 1 : -1);
      if (channelIndex < 0 || channelIndex >= this.channels.length) return false;
      const anchorMs = this.programs.startMsOf(before.channelIndex, before.slot);
      const slot = this.programs.slotAt(channelIndex, anchorMs);
      next = { channelIndex, slot: slot >= 0 ? slot : 0 };
    }

    this.cursor = next;
    this.cursorNotifications += 1;
    for (const listener of this.cursorListeners) listener(next);
    this.applyGeometry();
    this.scheduleSettle();
    return true;
  }

  /** Salta a un canale: serve alle categorie e allo zapping. */
  focusChannel(channelIndex: number): void {
    if (channelIndex < 0 || channelIndex >= this.channels.length) return;
    const slot = this.programs.slotAt(channelIndex, this.window.startMs);
    this.cursor = { channelIndex, slot: slot >= 0 ? slot : 0 };
    this.cursorNotifications += 1;
    for (const listener of this.cursorListeners) listener(this.cursor);
    this.applyGeometry();
    this.scheduleSettle();
  }

  /**
   * Salta a una cella precisa: serve al **mouse**.
   *
   * Le frecce si muovono di uno, e per loro basta `move`. Un clic invece arriva su una cella
   * qualsiasi, e senza questo metodo l'unico modo di raggiungerla sarebbe simulare le frecce che
   * servono ad arrivarci — cioè ricostruire la distanza fra due punti che il chiamante ha già.
   *
   * Lo slot si stringe dentro i programmi che ci sono: un canale senza EPG accetta il clic e resta
   * allo slot zero, invece di rifiutarlo. Rifiutarlo renderebbe il mouse inutile proprio dove serve
   * di più, cioè dove la guida non ha niente da mostrare.
   */
  focusAt(channelIndex: number, slot: number): void {
    if (channelIndex < 0 || channelIndex >= this.channels.length) return;
    const count = this.programs.programCountOf(channelIndex);
    const safeSlot = count === 0 ? 0 : Math.min(Math.max(slot, 0), count - 1);
    this.cursor = { channelIndex, slot: safeSlot };
    this.cursorNotifications += 1;
    for (const listener of this.cursorListeners) listener(this.cursor);
    this.applyGeometry();
    this.scheduleSettle();
  }

  /**
   * Ricalcola lo scorrimento e, **solo se la finestra è davvero cambiata**, avvisa chi
   * disegna le righe. È il punto in cui si decide se questa app va a 60 fps o no.
   */
  private applyGeometry(): void {
    const previousY = this.scrollYPx;
    const previousX = this.scrollXPx;

    this.scrollYPx = scrollToReveal(
      this.cursor.channelIndex,
      this.scrollYPx,
      this.viewport.heightPx,
      ROW_PITCH_PX,
      this.channels.length,
      this.edgeMarginPx,
    );

    const count = this.programs.programCountOf(this.cursor.channelIndex);
    if (count > 0) {
      const program = this.programs.materialize(this.cursor.channelIndex, this.cursor.slot);
      const layout = layoutProgram(program, this.window);
      this.scrollXPx = scrollToRevealSpan(
        layout.leftPx,
        layout.widthPx,
        this.scrollXPx,
        this.viewport.timelineWidthPx,
        windowWidthPx(this.window),
        0,
      );
    }

    const nextRange = this.computeRange();
    const rangeChanged =
      nextRange.firstIndex !== this.range.firstIndex || nextRange.endIndex !== this.range.endIndex;
    this.range = nextRange;

    if (rangeChanged || this.scrollYPx !== previousY || this.scrollXPx !== previousX) {
      this.windowNotifications += 1;
      const geometry = this.geometry;
      for (const listener of this.windowListeners) listener(geometry);
    }
  }

  private scheduleSettle(): void {
    if (this.settleHandle !== null) this.clearTimeoutImpl(this.settleHandle);
    this.settleHandle = this.setTimeoutImpl(() => {
      this.settleHandle = null;
      for (const listener of this.settleListeners) listener(this.cursor);
    }, this.settleMs);
  }

  dispose(): void {
    if (this.settleHandle !== null) this.clearTimeoutImpl(this.settleHandle);
    this.cursorListeners.clear();
    this.windowListeners.clear();
    this.settleListeners.clear();
  }
}
